import { defineStore } from 'pinia';
import SystemAnnouncementService from '@/services/systemAnnouncementService';

const DISMISSED_KEY = 'wusa.systemAnnouncements.dismissed';

const SEVERITY_RANK = { error: 0, warning: 1, info: 2, success: 3 };

function loadDismissed() {
  try {
    const raw = sessionStorage.getItem(DISMISSED_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

function persistDismissed(ids) {
  try {
    sessionStorage.setItem(DISMISSED_KEY, JSON.stringify(ids));
  } catch (e) {
    // storage unavailable — no-op
  }
}

export const useSystemAnnouncementsStore = defineStore('systemAnnouncements', {
  state: () => ({
    banners: [],
    dismissedIds: loadDismissed(),
    loaded: false,
    loading: false,
  }),

  getters: {
    activeBanners(state) {
      const dismissed = new Set(state.dismissedIds);
      return state.banners
        .filter((b) => !dismissed.has(b.id))
        .slice()
        .sort((a, b) => {
          const ra = SEVERITY_RANK[a.severity] ?? 99;
          const rb = SEVERITY_RANK[b.severity] ?? 99;
          if (ra !== rb) return ra - rb;
          return new Date(b.published_at || 0) - new Date(a.published_at || 0);
        });
    },
  },

  actions: {
    async fetch() {
      if (this.loading) return;
      this.loading = true;
      try {
        const service = SystemAnnouncementService.create();
        const response = await service.getActive();
        this.banners = response?.data?.banners || [];
        this.loaded = true;
      } finally {
        this.loading = false;
      }
    },

    dismiss(id) {
      if (this.dismissedIds.includes(id)) return;
      this.dismissedIds = [...this.dismissedIds, id];
      persistDismissed(this.dismissedIds);
    },

    resetDismissals() {
      this.dismissedIds = [];
      persistDismissed(this.dismissedIds);
    },
  },
});
